// store/slices/colorSlice.ts
import axios from "axios";
import { getSession } from "next-auth/react";

const debounce = (func: (...args: any[]) => void, delay: number) => {
  // Per-instance timer: each debounced fn keeps its own, so a metric-colour
  // save can't cancel a pending primary/secondary save (and vice versa).
  let timer: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

const updatePrimaryColorAPI = debounce(async (color: string) => {
  try {
    const session = await getSession();
    if (session) {
      await axios.post("/api/users/update-account-settings", {
        primaryColor: color,
      });
    }
  } catch (error) {
    console.error("Failed to update primary color:", error);
  }
}, 500);

const updateSecondaryColorsAPI = debounce(
  async (colors: Array<{ hex: string; rgb: string }>) => {
    try {
      const session = await getSession();
      if (session) {
        await axios.post("/api/users/update-account-settings", {
          secondaryColors: colors,
        });
      }
    } catch (error) {
      console.error("Failed to update secondary colors:", error);
    }
  },
  500,
);

const updateMetricColorsAPI = debounce(
  async (colors: Array<{ hex: string; rgb: string }>) => {
    try {
      const session = await getSession();
      if (session) {
        await axios.post("/api/users/update-account-settings", {
          metricColors: colors,
        });
      }
    } catch (error) {
      console.error("Failed to update metric colors:", error);
    }
  },
  500,
);

const defaultColor = "51 109 255";

// In dark mode the charcoal secondary ramp (globals.css `html.dark`) is pinned
// with `!important`, and the visible surfaces (.bg-white card, inputs, borders)
// are hardcoded — so remapping only the CSS vars does nothing you can see. To
// make a chosen Secondary Theme actually re-skin dark mode, mirror the palette
// (ordered dark -> light) into a managed <style>: the DARK end drives the token
// surfaces AND re-skins the hardcoded surface rules; the LIGHT end drives text.
// Appended last, so its `!important` rules win over the globals.css defaults.
// Cleared for the built-in default palette, falling back to the charcoal base.
const DARK_SECONDARY_STYLE_ID = "secondary-dark-theme";
const applyDarkSecondaryOverride = (
  colors: Array<{ hex: string; rgb: string }>,
) => {
  if (typeof document === "undefined") return;
  let styleEl = document.getElementById(
    DARK_SECONDARY_STYLE_ID,
  ) as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.id = DARK_SECONDARY_STYLE_ID;
    document.head.appendChild(styleEl);
  }

  // Default palette (or a malformed one) → no override; keep the charcoal base.
  const isDefault =
    colors.length === initialState.secondaryColors.length &&
    colors.every(
      (c, i) =>
        c.hex.toLowerCase() ===
        initialState.secondaryColors[i].hex.toLowerCase(),
    );
  if (isDefault || colors.length < 5) {
    styleEl.textContent = "";
    return;
  }

  // colors are ordered dark -> light: d0 (deepest surface) … d4 (text). Text
  // tokens (1-3) draw from the light end so captions stay readable on a dark
  // surface; surface/border tokens (5-7) draw from the dark end.
  const [d0, d1, d2, d3, d4] = colors.map((c) => c.rgb);
  styleEl.textContent = `
    html.dark {
      --color-secondary-1: ${d4} !important;
      --color-secondary-2: ${d3} !important;
      --color-secondary-3: ${d3} !important;
      --color-secondary-4: ${d2} !important;
      --color-secondary-5: ${d1} !important;
      --color-secondary-6: ${d1} !important;
      --color-secondary-7: ${d0} !important;
    }
    html.dark .bg-white, html.dark .bg-kd-white { background-color: rgb(${d0}) !important; }
    html.dark input, html.dark textarea, html.dark select {
      background-color: rgb(${d0}) !important;
      border-color: rgb(${d1}) !important;
    }
    html.dark .bg-gray-100 { background-color: rgb(${d0}) !important; }
    html.dark .bg-gray-200 { background-color: rgb(${d1}) !important; }
    html.dark .border-kd-secondary-6,
    html.dark .border-gray-200,
    html.dark .border-gray-300 { border-color: rgb(${d1}) !important; }
  `;
};

export interface ColorState {
  primaryColor: string;
  secondaryColors: Array<{ hex: string; rgb: string }>;
  // Categorical palette for metric/series colours (charts read these). Slots
  // map to --color-metric-1..5; the default mirrors the dashboard's existing
  // ecommerce series so the out-of-the-box look is unchanged.
  metricColors: Array<{ hex: string; rgb: string }>;
}

export interface ColorActions {
  setPrimaryColor: (color: string, persist?: boolean) => void;
  setSecondaryColors: (
    colors: Array<{ hex: string; rgb: string }>,
    persist?: boolean,
  ) => void;
  setMetricColors: (
    colors: Array<{ hex: string; rgb: string }>,
    persist?: boolean,
  ) => void;
}

export const initialState: ColorState = {
  primaryColor: defaultColor,
  secondaryColors: [
    { hex: "#05004e", rgb: "5 0 78" },
    { hex: "#444a6d", rgb: "68 74 109" },
    { hex: "#96a5b8", rgb: "150 165 184" },
    { hex: "#d8d8d8", rgb: "216 216 216" },
    { hex: "#ececec", rgb: "236 236 236" },
    { hex: "#edf2f6", rgb: "237 242 246" },
    { hex: "#f9fafb", rgb: "249 250 251" },
  ],
  metricColors: [
    { hex: "#00E096", rgb: "0 224 150" },
    { hex: "#0E9AFF", rgb: "14 154 255" },
    { hex: "#BF83FF", rgb: "191 131 255" },
    { hex: "#FF947A", rgb: "255 148 122" },
    { hex: "#FA5A7D", rgb: "250 90 125" },
  ],
};

export const colorSlice = (
  set: any,
): { data: ColorState; actions: ColorActions } => ({
  data: initialState,
  actions: {
    // `persist` (default true) saves to the server. Pass false to APPLY ONLY —
    // used when rehydrating / switching workspaces, so re-applying a workspace's
    // colours doesn't echo a redundant write (or overwrite it with defaults).
    setPrimaryColor: (color: string, persist = true) => {
      set((state: any) => {
        state.data.primaryColor = color;
      });
      if (typeof window !== "undefined") {
        document.documentElement.style.setProperty("--color-primary", color);
      }
      if (persist) updatePrimaryColorAPI(color);
    },
    setSecondaryColors: (
      colors: Array<{ hex: string; rgb: string }>,
      persist = true,
    ) => {
      set((state: any) => {
        state.data.secondaryColors = colors;
      });
      if (typeof window !== "undefined") {
        colors.forEach((color, index) => {
          document.documentElement.style.setProperty(
            `--color-secondary-${index + 1}`,
            color.rgb,
          );
        });
        // Reversed ramp for dark mode so the palette applies there too.
        applyDarkSecondaryOverride(colors);
      }
      if (persist) updateSecondaryColorsAPI(colors);
    },
    setMetricColors: (
      colors: Array<{ hex: string; rgb: string }>,
      persist = true,
    ) => {
      set((state: any) => {
        state.data.metricColors = colors;
      });
      if (typeof window !== "undefined") {
        colors.forEach((color, index) => {
          document.documentElement.style.setProperty(
            `--color-metric-${index + 1}`,
            color.rgb,
          );
        });
      }
      if (persist) updateMetricColorsAPI(colors);
    },
  },
});
